import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, Send, AlertCircle } from "lucide-react";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { searchUsersForNotification } from "@/services/adminNotificationService";

interface SendPrivateNotificationModalProps {
  open: boolean;
  onClose: () => void;
  preselectedUserId?: string;
  preselectedUserName?: string;
}

export const SendPrivateNotificationModal = ({
  open,
  onClose,
  preselectedUserId,
  preselectedUserName
}: SendPrivateNotificationModalProps) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState(preselectedUserName || "");
  const [selectedUserId, setSelectedUserId] = useState(preselectedUserId || "");
  const [userResults, setUserResults] = useState<Array<{ id: string; full_name: string; email: string }>>([]);
  const [showResults, setShowResults] = useState(false);

  const { sendPrivate, isSending } = useAdminNotifications();

  useEffect(() => {
    if (preselectedUserId && preselectedUserName) {
      setSelectedUserId(preselectedUserId);
      setSearchTerm(preselectedUserName);
    }
  }, [preselectedUserId, preselectedUserName]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 2 && !preselectedUserId) {
        const results = await searchUsersForNotification(searchTerm);
        setUserResults(results);
        setShowResults(true);
      } else {
        setUserResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, preselectedUserId]);

  const handleSelectUser = (user: { id: string; full_name: string; email: string }) => {
    setSelectedUserId(user.id);
    setSearchTerm(user.full_name);
    setShowResults(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId || !title.trim() || !message.trim()) {
      return;
    }

    try {
      await sendPrivate.mutateAsync({
        targetUserId: selectedUserId,
        title: title.trim(),
        message: message.trim()
      });

      setTitle("");
      setMessage("");
      setSearchTerm("");
      setSelectedUserId("");
      onClose();
    } catch (error) {
      // Error handled by hook
    }
  };

  const isValid = selectedUserId && title.trim().length > 0 && message.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Enviar Notificación Privada
          </DialogTitle>
          <DialogDescription>
            Envía una notificación directa a un usuario específico. El usuario la verá en su panel de notificaciones.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-search">Usuario Destinatario *</Label>
            <div className="relative">
              <Input
                id="user-search"
                placeholder="Buscar usuario por nombre o email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (!preselectedUserId) {
                    setSelectedUserId("");
                  }
                }}
                disabled={!!preselectedUserId}
              />
              {showResults && userResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {userResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="font-medium">{user.full_name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!selectedUserId && searchTerm.length >= 2 && userResults.length === 0 && (
              <p className="text-sm text-muted-foreground">No se encontraron usuarios</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              placeholder="Ej: Actualización importante de tu cuenta"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground text-right">
              {title.length}/100 caracteres
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensaje *</Label>
            <Textarea
              id="message"
              placeholder="Escribe el mensaje que verá el usuario..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={5}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground text-right">
              {message.length}/500 caracteres
            </p>
          </div>

          {isValid && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Vista previa:</strong> El usuario recibirá una notificación con el título "{title}" 
                y podrá ver el mensaje completo en su panel de notificaciones.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!isValid || isSending}
            >
              <Send className="h-4 w-4 mr-2" />
              {isSending ? "Enviando..." : "Enviar Notificación"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
